import {
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
  useId,
} from 'react'

import type { Common, FieldProps } from './Field.types'

/** Label + control + help/error, wired for accessibility. */
export function Field({
  label,
  help,
  error,
  required,
  className,
  extra,
  children,
}: FieldProps) {
  const id = useId()
  const describedBy = error ? `${id}-error` : help ? `${id}-help` : undefined

  return (
    <div className={['field', className].filter(Boolean).join(' ')}>
      {label && (
        <label className="field__label" htmlFor={id} id={`${id}-label`}>
          <span title={typeof label === 'string' ? label : undefined}>
            {label}
          </span>
          {required && <span className="field__required">*</span>}
          {extra && <span style={{ marginLeft: 'auto' }}>{extra}</span>}
        </label>
      )}
      {children(id, describedBy)}
      {error ? (
        <p className="field__error" id={`${id}-error`}>
          {error}
        </p>
      ) : help ? (
        <p className="field__help" id={`${id}-help`}>
          {help}
        </p>
      ) : null}
    </div>
  )
}

export function TextInput({
  label,
  help,
  error,
  className,
  extra,
  required,
  ...rest
}: Common & InputHTMLAttributes<HTMLInputElement>) {
  return (
    <Field
      label={label}
      help={help}
      error={error}
      required={required}
      className={className}
      extra={extra}
    >
      {(id, describedBy) => (
        <input
          id={id}
          className="control"
          aria-invalid={!!error || undefined}
          aria-describedby={describedBy}
          required={required}
          {...rest}
        />
      )}
    </Field>
  )
}

export function TextArea({
  label,
  help,
  error,
  className,
  extra,
  required,
  ...rest
}: Common & TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return (
    <Field
      label={label}
      help={help}
      error={error}
      required={required}
      className={className}
      extra={extra}
    >
      {(id, describedBy) => (
        <textarea
          id={id}
          className="control"
          aria-invalid={!!error || undefined}
          aria-describedby={describedBy}
          required={required}
          {...rest}
        />
      )}
    </Field>
  )
}

export function Select({
  label,
  help,
  error,
  className,
  extra,
  required,
  options,
  placeholder,
  ...rest
}: Common &
  SelectHTMLAttributes<HTMLSelectElement> & {
    options: Array<{ value: string; label: string }>
    placeholder?: string
  }) {
  return (
    <Field
      label={label}
      help={help}
      error={error}
      required={required}
      className={className}
      extra={extra}
    >
      {(id, describedBy) => (
        <select
          id={id}
          className="control"
          aria-invalid={!!error || undefined}
          aria-describedby={describedBy}
          required={required}
          {...rest}
        >
          {placeholder !== undefined && <option value="">{placeholder}</option>}
          {options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      )}
    </Field>
  )
}

export function Checkbox({
  label,
  ...rest
}: { label: ReactNode } & InputHTMLAttributes<HTMLInputElement>) {
  return (
    <label className="check">
      <input type="checkbox" {...rest} />
      <span>{label}</span>
    </label>
  )
}

export function Switch({
  label,
  ...rest
}: { label: ReactNode } & InputHTMLAttributes<HTMLInputElement>) {
  return (
    <label className="switch">
      <input type="checkbox" role="switch" {...rest} />
      <span>{label}</span>
    </label>
  )
}
